import { useEffect, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Select, message } from "antd";
import moment from "moment";
import type { Moment } from "moment";
import CountryDropdown from "../../account/misc/CountryDropdown";
import { UnitedStates } from "../../../util/countries";
import { getUSStates } from "../../../util/utils";
import YextApi, { ensureHttps, yextErrorMessage } from "../../../api/YextApi";
import "./ProfileIdentitySection.scss";

const { TextArea } = Input;

const BUSINESS_CATEGORIES = ["Restaurant", "Hospitality", "Retail"];

// Static list — built once rather than on every render.
const US_STATE_OPTIONS = getUSStates().map((s) => ({ value: s.key, label: s.value }));

interface IdentityForm {
  businessName: string;
  alternateNames: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  latitude: string;
  longitude: string;
  primaryPhone: string;
  secondaryPhone: string;
  website: string;
  descriptionShort: string;
  descriptionLong: string;
  categories: string[];
  openingDate: Moment | null;
}

const EMPTY: IdentityForm = {
  businessName: "",
  alternateNames: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  latitude: "",
  longitude: "",
  primaryPhone: "",
  secondaryPhone: "",
  website: "",
  descriptionShort: "",
  descriptionLong: "",
  categories: [],
  openingDate: null,
};

interface Props {
  locationId?: number;
}

export function ProfileIdentitySection({ locationId }: Props) {
  const [form, setForm] = useState<IdentityForm>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<IdentityForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const loaded: IdentityForm = {
        businessName: data.businessName ?? "",
        alternateNames: data.alternateNames ?? "",
        street: data.street ?? "",
        street2: data.street2 ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        zip: data.zip ?? "",
        country: data.country ?? "",
        latitude: data.latitude ?? "",
        longitude: data.longitude ?? "",
        primaryPhone: data.primaryPhone ?? "",
        secondaryPhone: data.secondaryPhone ?? "",
        website: data.website ?? "",
        descriptionShort: data.descriptionShort ?? "",
        descriptionLong: data.descriptionLong ?? "",
        categories: data.categories ?? [],
        openingDate: data.openingDate ? moment(data.openingDate, "YYYY-MM-DD") : null,
      };
      setForm(loaded);
      setSavedSnapshot(loaded);
    });
  }, [locationId]);

  function update<K extends keyof IdentityForm>(key: K, value: IdentityForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleCategory(category: string, checked: boolean) {
    setForm((f) => ({
      ...f,
      categories: checked
        ? [...f.categories, category]
        : f.categories.filter((c) => c !== category),
    }));
  }

  function discardChanges() {
    setForm(savedSnapshot);
  }

  async function saveChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      await YextApi.syncLocation(locationId, {
        businessName: form.businessName,
        alternateNames: form.alternateNames,
        street: form.street,
        street2: form.street2,
        city: form.city,
        state: form.state,
        zip: form.zip,
        country: form.country,
        latitude: form.latitude,
        longitude: form.longitude,
        primaryPhone: form.primaryPhone,
        secondaryPhone: form.secondaryPhone,
        website: form.website,
        descriptionShort: form.descriptionShort,
        descriptionLong: form.descriptionLong,
        categories: form.categories,
        openingDate: form.openingDate ? form.openingDate.format("YYYY-MM-DD") : undefined,
      });
      setSavedSnapshot(form);
      message.success("Identity saved.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save identity."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-identity">
      <section className="pi-group">
        <h3 className="pi-group-title">Business Identity</h3>

        <Field label="Business Name">
          <Input
            placeholder="Business Name"
            size="large"
            value={form.businessName}
            onChange={(e) => update("businessName", e.target.value)}
          />
        </Field>

        <Field label="Alternate Business Names">
          <Input
            placeholder="Alternate Business Names"
            size="large"
            value={form.alternateNames}
            onChange={(e) => update("alternateNames", e.target.value)}
          />
        </Field>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Location</h3>

        <Field label="Street Address">
          <Input
            placeholder="Address"
            size="large"
            value={form.street}
            onChange={(e) => update("street", e.target.value)}
          />
        </Field>

        <Field label="Street Address Line 2 (Optional)">
          <Input
            placeholder="Address Line 2"
            size="large"
            value={form.street2}
            onChange={(e) => update("street2", e.target.value)}
          />
        </Field>

        <div className="pi-row">
          <Field label="City">
            <Input
              placeholder="City"
              size="large"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
            />
          </Field>
          <Field label="State">
            {form.country === UnitedStates ? (
              <Select
                showSearch
                size="large"
                style={{ width: "100%" }}
                placeholder="State"
                value={form.state || undefined}
                onChange={(v) => update("state", v)}
                optionFilterProp="label"
                options={US_STATE_OPTIONS}
              />
            ) : (
              <Input
                placeholder="State"
                size="large"
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="pi-row">
          <Field label="Zip/Postal Code">
            <Input
              placeholder="Zip/Postal Code"
              size="large"
              value={form.zip}
              onChange={(e) => update("zip", e.target.value)}
            />
          </Field>
          <Field label="Country">
            <CountryDropdown
              defaultValue={form.country}
              onChange={(v) => update("country", v)}
            />
          </Field>
        </div>

        <div className="pi-row">
          <Field label="Latitude">
            <Input
              placeholder="Latitude"
              size="large"
              value={form.latitude}
              onChange={(e) => update("latitude", e.target.value)}
            />
          </Field>
          <Field label="Longitude">
            <Input
              placeholder="Longitude"
              size="large"
              value={form.longitude}
              onChange={(e) => update("longitude", e.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">Contact</h3>

        <div className="pi-row">
          <Field label="Primary Phone">
            <Input
              placeholder="Primary Phone"
              size="large"
              value={form.primaryPhone}
              onChange={(e) => update("primaryPhone", e.target.value)}
            />
          </Field>
          <Field label="Secondary Phone">
            <Input
              placeholder="Secondary Phone"
              size="large"
              value={form.secondaryPhone}
              onChange={(e) => update("secondaryPhone", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Website">
          <Input
            placeholder="Website"
            size="large"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
            onBlur={(e) => update("website", ensureHttps(e.target.value))}
          />
        </Field>
      </section>

      <section className="pi-group">
        <h3 className="pi-group-title">About Your Business</h3>

        <Field label="Business Description (Short)">
          <Input
            placeholder="Business Description (Short)"
            size="large"
            value={form.descriptionShort}
            onChange={(e) => update("descriptionShort", e.target.value)}
          />
        </Field>

        <Field label="Business Description (Long)">
          <TextArea
            placeholder="Business Description (Long)"
            rows={5}
            value={form.descriptionLong}
            onChange={(e) => update("descriptionLong", e.target.value)}
          />
        </Field>

        <div className="pi-categories">
          <span className="pi-field-label">Business Categories</span>
          <div className="pi-categories-list">
            {BUSINESS_CATEGORIES.map((cat) => (
              <Checkbox
                key={cat}
                checked={form.categories.includes(cat)}
                onChange={(e) => toggleCategory(cat, e.target.checked)}
              >
                {cat}
              </Checkbox>
            ))}
          </div>
        </div>

        <Field label="Opening Date (Where supported)">
          <DatePicker
            placeholder="Opening Date"
            size="large"
            className="pi-date-picker"
            value={form.openingDate}
            onChange={(d) => update("openingDate", d)}
            format="MMM D, YYYY"
          />
        </Field>
      </section>

      <footer className="pi-actions">
        <Button className="pi-discard-btn" size="large" onClick={discardChanges} disabled={saving}>
          Discard Changes
        </Button>
        <Button
          type="primary"
          danger
          className="pi-save-btn"
          size="large"
          onClick={saveChanges}
          loading={saving}
        >
          Save Changes
        </Button>
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="pi-field">
      <span className="pi-field-label">{label}</span>
      {children}
    </label>
  );
}
